import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";

import { ContactId } from "@goose/schema/Salesforce";

import { Breadcrumbs } from "~/components/admin/Breadcrumbs";

import {
  ContactIcon,
  EvaluationIcon,
  OpportunityIcon,
  RequirementSetIcon,
} from "~/components/admin/Icons";

import { PageHeader } from "~/components/admin/PageHeader";
import { StatCard } from "~/components/admin/StatCard";
import { QueryState } from "~/components/QueryState";
import { TRPCClientErrorDisplay } from "~/components/TRPCClientErrorDisplay";
import { Button } from "~/components/ui/Button";
import { Spinner } from "~/components/ui/Spinner";
import { useTRPC } from "~/lib/trpc";

export const Route = createFileRoute(
  "/_authenticated/admin/organizations/$organizationId/contacts/$contactId",
)({
  component: ContactPage,
  params: {
    parse: ({ contactId }) => {
      return {
        contactId: ContactId.parse(contactId),
      };
    },
  },
});

function ContactPage() {
  const trpc = useTRPC();
  const { organizationId, contactId } = Route.useParams();

  const organizationQuery = useQuery(
    trpc.salesforce.organizations.get.queryOptions({
      id: organizationId,
    }),
  );

  const evaluationsCount = useQuery(
    trpc.contacts.evaluations.count.queryOptions({
      organizationId,
      contactId,
    }),
  );

  const opportunitiesCount = useQuery(
    trpc.opportunities.count.queryOptions({
      organizationId,
      contactId,
    }),
  );

  const requirementSetsCount = useQuery(
    trpc.opportunities.requirements.count.queryOptions({
      organizationId,
      contactId,
    }),
  );

  return (
    <QueryState
      result={organizationQuery}
      loading={
        <div className="flex justify-center items-center py-24">
          <Spinner size="lg" />
        </div>
      }
      error={(error) => <TRPCClientErrorDisplay error={error} />}
    >
      {(organization) => (
        <div className="space-y-6">
          <PageHeader
            breadcrumbs={
              <Breadcrumbs>
                <Link to="/admin">Admin</Link>
                <Link to="/admin/organizations">Organizations</Link>
                <Link
                  to="/admin/organizations/$organizationId"
                  params={{ organizationId }}
                >
                  {organization.name}
                </Link>
                <span>{contactId}</span>
              </Breadcrumbs>
            }
            icon={ContactIcon}
            title="Contact"
            actions={
              <Button
                as="a"
                variant="outline"
                size="sm"
                href={`${organization.url}/${contactId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open in Salesforce
              </Button>
            }
          />

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <StatCard
              label="Evaluations"
              value={evaluationsCount.data ?? 0}
              icon={<EvaluationIcon className="h-7 w-7" />}
              isLoading={evaluationsCount.isLoading}
              error={evaluationsCount.error}
            />
            <StatCard
              label="Opportunities"
              value={opportunitiesCount.data ?? 0}
              icon={<OpportunityIcon className="h-7 w-7" />}
              isLoading={opportunitiesCount.isLoading}
              error={opportunitiesCount.error}
            />
            <StatCard
              label="Requirement Sets"
              value={requirementSetsCount.data ?? 0}
              icon={<RequirementSetIcon className="h-7 w-7" />}
              isLoading={requirementSetsCount.isLoading}
              error={requirementSetsCount.error}
            />
          </div>

          <Outlet />
        </div>
      )}
    </QueryState>
  );
}
